import { premiumRequestRecovery } from './premium-request-recovery.js';
import { createUuid } from './create-uuid.js';

/** Provider mutations retain their exact request after an uncertain response. */
export function premiumBillingControls(request, messages, checkAccount = () => true, applyAccess = () => {}, accountId = '') {
    let confirmationTrigger;
    return {
        state: null,
        loading: false,
        working: false,
        error: '',
        notice: '',
        ...premiumRequestRecovery(accountId, 'billing', payload => payload &&
            typeof payload.request_id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(payload.request_id) &&
            (payload.action === 'cancel' || payload.action === 'resume' || (payload.action === 'change' &&
                typeof payload.expected_period_end === 'string' && payload.expected_period_end !== '' &&
                (payload.plan_id === null || (typeof payload.plan_id === 'string' && payload.plan_id !== '' &&
                    Number.isSafeInteger(payload.expected_unit_amount) && payload.expected_unit_amount >= 0 &&
                    typeof payload.expected_currency === 'string' && /^[a-z]{3}$/i.test(payload.expected_currency))))), messages, 'request_id'),
        confirmation: null,

        async load() {
            if (!checkAccount() || this.working || this.loading) return;
            this.loading = true;
            this.confirmation = null;
            this.error = '';
            this.notice = '';
            this.restorePending();
            try {
                const state = await request('/plus-plan-change', { method: 'GET' });
                if (!checkAccount()) return;
                this.state = state;
                applyAccess(this.state.access);
            } catch {
                this.state = null;
                this.error = messages.loadError;
            } finally {
                this.loading = false;
            }
        },

        get blocked() {
            return this.recoveryBlocked || this.working || this.loading || this.requestToRetry !== null || this.state?.operation_pending === true;
        },

        // can_cancel is provider capability, so the scheduled end state comes from billing.state.
        get cancellationScheduled() {
            return this.state?.billing?.state === 'cancellation_scheduled';
        },

        get canResume() {
            return this.cancellationScheduled && this.state.can_resume === true;
        },

        prepare(action, planId = null, trigger = null) {
            if (!checkAccount() || !this.restorePending() || this.blocked || !this.state || this.cancellationScheduled) return;
            if (action === 'cancel' && (!this.state.can_cancel || this.state.cancellation_review_required)) return;
            if (action === 'change') {
                if (!this.state.can_change_plan || (planId !== null && planId === this.state.current_plan_id)) return;
                if (planId !== null && !this.state.change_offers?.some(offer => offer.plan_id === planId)) return;
                if (planId === null && !this.state.pending_change) return;
            }
            const requestId = createUuid();
            if (!requestId) {
                this.error = messages.loadError;
                return;
            }
            confirmationTrigger = trigger;
            this.notice = '';
            this.confirmation = { action, request_id: requestId };
            if (action === 'change') {
                this.confirmation.plan_id = planId;
                this.confirmation.expected_period_end = this.state.period_end;
                if (planId !== null) {
                    const offer = this.state.change_offers.find(item => item.plan_id === planId);
                    this.confirmation.expected_unit_amount = offer.unit_amount;
                    this.confirmation.expected_currency = offer.currency;
                }
            }
        },

        dismiss(nextTick) {
            this.confirmation = null;
            nextTick(() => {
                if (confirmationTrigger?.isConnected) confirmationTrigger.focus();
                confirmationTrigger = null;
            });
        },

        /** The caller resolves the status element at click time, because the control that started the request can unmount. */
        async settle(action, status, nextTick) {
            await action();
            nextTick(() => {
                if ((this.notice || this.error) && status?.isConnected) status.focus();
            });
        },

        /** Resume needs no confirmation step. It undoes a cancellation and charges nothing before renewal. */
        async resume() {
            if (!checkAccount() || !this.restorePending() || this.blocked || !this.canResume) return;
            const requestId = createUuid();
            if (!requestId) {
                this.error = messages.loadError;
                return;
            }
            await this.submit({ action: 'resume', request_id: requestId });
        },

        async submit(intent = null) {
            if (!checkAccount() || this.working || this.loading) return;
            if (!this.restorePending()) return;
            const payload = this.requestToRetry || intent || this.confirmation;
            if (!payload) return;
            const body = this.persistPending(payload);
            if (body === null) return;
            this.working = true;
            this.error = '';
            this.notice = '';
            this.confirmation = null;
            let refusal = null;
            try {
                const state = await request('/plus-plan-change', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                });
                if (!checkAccount()) return;
                this.state = state;
                applyAccess(this.state.access);
                this.clearPending(body);
                this.notice = { resume: messages.resumed, cancel: messages.canceled, change: messages.changed }[payload.action] || '';
            } catch (error) {
                if (!checkAccount()) return;
                // A 409 rejects the intent. Pending operations return 503 and retain their request identity.
                if ([400, 409, 422].includes(error.status)) {
                    this.clearPending(body);
                    this.state = null;
                    refusal = error.displayMessage || messages.refused || messages.loadError;
                }
                this.error = messages.loadError;
            } finally {
                this.working = false;
            }
            if (refusal !== null) {
                await this.load();
                if (checkAccount()) this.error = refusal;
            }
        },
    };
}
